import React, { useState } from 'react';
import classes from './Chat.module.css';

export type SamsaraMessage = {
  driverId: number;
  driverSamsaraId: string;
  text: string;
  sentAtMs: number;
  isRead: boolean;
  senderName: string;
  senderType: 'driver' | 'dispatch';
};

export type ChatProps = {
  messages: SamsaraMessage[];
  isLoading: boolean;
  defaultMessage?: string;
  onSendMessage?: (message: string) => Promise<void>;
};

export default function Chat({
  messages = [],
  isLoading,
  defaultMessage,
  onSendMessage,
}: ChatProps) {
  const [messageText, setMessageText] = useState(defaultMessage || '');
  const [isSending, setIsSending] = useState(false);

  const formatTimestamp = (sentAtMs: number) => {
    const date = new Date(sentAtMs);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const sortedMessages = [...messages].sort((a, b) => a.sentAtMs - b.sentAtMs);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim() || !onSendMessage || isSending) return;

    setIsSending(true);
    try {
      await onSendMessage(messageText.trim());
      setMessageText('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className={classes.chatContainer}>
        <div className={classes.loadingMessage}>Loading messages...</div>
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className={classes.chatContainer}>
        <div className={classes.noMessages}>No messages available.</div>
        {onSendMessage && (
          <form
            onSubmit={handleSendMessage}
            className={classes.messageInputForm}
          >
            <div className={classes.inputContainer}>
              <textarea
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                placeholder="Type a message..."
                className={classes.messageInput}
                disabled={isSending}
                rows={3}
              />
              <button
                type="submit"
                disabled={!messageText.trim() || isSending}
                className={classes.sendButton}
              >
                {isSending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className={classes.chatContainer}>
      <ul className={classes.messagesList}>
        {sortedMessages.map((msg, index) => (
          <li
            key={index}
            className={`${classes.messageItem} ${classes[msg.senderType]}`}
          >
            <div
              className={`${classes.messageBubble} ${classes[msg.senderType]}`}
            >
              <div className={classes.messageHeader}>
                <span className={classes.senderName}>{msg.senderName}</span>
                <span className={classes.messageTime}>
                  {formatTimestamp(msg.sentAtMs)}
                </span>
              </div>
              <p className={classes.messageText}>{msg.text}</p>
              {msg.senderType === 'dispatch' && (
                <div className={classes.messageFooter}>
                  <span className={classes.readIndicator}>
                    {msg.isRead ? (
                      <span className={classes.checkmark}>✓</span>
                    ) : (
                      <span>Sent</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>{' '}
      {onSendMessage && (
        <form onSubmit={handleSendMessage} className={classes.messageInputForm}>
          <div className={classes.inputContainer}>
            <textarea
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              placeholder="Type a message..."
              className={classes.messageInput}
              disabled={isSending}
              rows={3}
            />
            <button
              type="submit"
              disabled={!messageText.trim() || isSending}
              className={classes.sendButton}
            >
              {isSending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
